import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Check, X } from 'lucide-react';

/**
 * Drop-in replacement for a native <select> that supports keyboard search,
 * type-to-filter, and clear button. Brand-matched (orange focus ring).
 *
 * Props:
 *  - value: current value (string)
 *  - onChange: (newValue: string) => void
 *  - options: Array<string> OR Array<{value, label, hint?}>
 *  - placeholder?: string  (shown when nothing selected)
 *  - required?: boolean
 *  - disabled?: boolean
 *  - clearable?: boolean (default true if value is set)
 *  - allowCustom?: boolean (default false) — lets user accept their typed text
 *  - testid?: string
 */
const SearchableSelect = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Select...',
  required = false,
  disabled = false,
  clearable = true,
  allowCustom = false,
  testid,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Normalise options into {value,label} objects
  const norm = useMemo(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label ?? o.value, hint: o.hint })),
    [options]
  );

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return norm;
    return norm.filter((o) => o.label.toLowerCase().includes(qq) || String(o.value).toLowerCase().includes(qq));
  }, [norm, q]);

  const current = norm.find((o) => o.value === value);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => { setHighlight(0); }, [q]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(filtered.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) pick(filtered[highlight].value);
      else if (allowCustom && q.trim()) pick(q.trim());
    } else if (e.key === 'Escape') { setOpen(false); setQ(''); }
  };

  return (
    <div className={`relative ${className}`} ref={wrapRef} data-testid={testid}>
      {/* Hidden native input for required validation */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          required
          value={value || ''}
          onChange={() => {}}
          className="sr-only pointer-events-none absolute -z-10"
          style={{ opacity: 0, height: 0, width: 0 }}
        />
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={`w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white text-left flex items-center justify-between gap-2
          focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-gray-300'}
        `}
        data-testid={testid ? `${testid}-trigger` : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`flex-1 truncate ${current ? 'text-[#1A2A4F]' : 'text-gray-400'}`}>
          {current ? current.label : placeholder}
        </span>
        <span className="flex items-center gap-1">
          {clearable && value && !disabled && (
            <X
              className="w-3.5 h-3.5 text-gray-400 hover:text-[#1A2A4F]"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              data-testid={testid ? `${testid}-clear` : undefined}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-2xl overflow-hidden" data-testid={testid ? `${testid}-dropdown` : undefined}>
          <div className="flex items-center px-3 py-2 border-b border-gray-100">
            <Search className="w-3.5 h-3.5 text-gray-400 mr-2 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder="Search…"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid={testid ? `${testid}-search` : undefined}
            />
            {q && <X className="w-3.5 h-3.5 text-gray-400 hover:text-[#1A2A4F] cursor-pointer ml-1" onClick={() => setQ('')} />}
          </div>
          <ul role="listbox" className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-400">
                {allowCustom && q.trim() ? <>Press Enter to use "<strong>{q}</strong>"</> : 'No matches'}
              </li>
            ) : (
              filtered.map((o, i) => {
                const sel = o.value === value;
                return (
                  <li
                    key={o.value || o.label}
                    role="option"
                    aria-selected={sel}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(o.value)}
                    className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-2 ${
                      i === highlight ? 'bg-[#F5A623]/10 text-[#1A2A4F]' : 'text-[#1A2A4F] hover:bg-gray-50'
                    } ${sel ? 'font-semibold' : ''}`}
                    data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                  >
                    <span className="flex-1 truncate">
                      {o.label}
                      {o.hint && <span className="ml-2 text-xs text-gray-400">{o.hint}</span>}
                    </span>
                    {sel && <Check className="w-3.5 h-3.5 text-[#F5A623]" />}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
